import { useEffect, RefObject } from 'react';

export type UseOutsideClickOptions = {
  capture?: boolean;
  includeEscape?: boolean;
  enabled?: boolean;
  // additional refs that should be treated as inside (optional)
  excludes?: RefObject<HTMLElement>[];
};

/**
 * useOutsideClick
 * Calls `handler` when a mousedown/touchstart occurs outside the `ref` element
 * or when Escape is pressed (if includeEscape is true).
 */
export default function useOutsideClick(
  ref: RefObject<HTMLElement | null>,
  handler: (event?: Event | KeyboardEvent) => void,
  options: UseOutsideClickOptions = {}
) {
  const { capture = false, includeEscape = true, enabled = true, excludes = [] } = options;

  useEffect(() => {
    if (!enabled) return;

    const isInside = (target: Node | null) => {
      if (!target) return false;
      if (!ref || !ref.current) return false;
      if (ref.current.contains(target)) return true;
      for (const ex of excludes) {
        if (ex && ex.current && ex.current.contains(target)) return true;
      }
      return false;
    };

    const onDocMouse = (ev: MouseEvent | TouchEvent) => {
      const target = ev.target as Node | null;
      if (!isInside(target)) handler(ev);
    };

    const onDocKey = (ev: KeyboardEvent) => {
      if (includeEscape && ev.key === 'Escape') handler(ev);
    };

    document.addEventListener('mousedown', onDocMouse, capture);
    document.addEventListener('touchstart', onDocMouse, capture);
    if (includeEscape) document.addEventListener('keydown', onDocKey, capture);

    return () => {
      document.removeEventListener('mousedown', onDocMouse, capture);
      document.removeEventListener('touchstart', onDocMouse, capture);
      if (includeEscape) document.removeEventListener('keydown', onDocKey, capture);
    };
  }, [ref, handler, capture, includeEscape, enabled, ...(options.excludes || [])]);
}
